import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject } from 'react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { publicAppSettingsKey } from '@/lib/queryClient';
import { getUploadedClubLogoUrl } from '@/lib/matchLogos';
import { FeedbackAuthoringHost, type FeedbackAuthoringHostHandle } from './FeedbackAuthoringHost';
import { FeedbackConversationHost } from './FeedbackConversationHost';
import { useCanonicalScope } from './useCanonicalFeedback';
import { useRoster } from '@/features/coach/hooks/useRoster';
import { useMatches } from '@/features/matches/useMatches';
import { CanonicalRecapHost } from '@/features/recaps/CanonicalRecapHost';
import { useRecapScope } from '@/features/recaps/useCanonicalRecap';
import { isCurrentDraftContext, useComposerDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { captureRecapBoundary, getAppSettings, observeAuthTokenChanges } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { feedbackNavigationTarget } from '@/lib/feedbackNavigation';
import { initializeResourceEditorContinuation, listResourceEditorContinuations, loadResourceEditorContinuation, resourceEditorContinuationKind,
  type ResourceEditorReturnTarget } from '@/lib/resourceEditorContinuation';
import { createResourceLibraryAction, type ResourceLibraryAction } from '@/features/resource-editor/resourceLibraryActions';
import { ResourceKindChooser } from '@/features/resource-editor/ResourceKindChooser';
import { resourceEditorHref } from '@/features/resource-editor/resourceEditorLinks';
import type { ResourceEditorLocation, ResourceEditorSlot } from '@/features/resource-editor/useResourceEditor';
import { initialResourceLibraryState, type ResourceLibraryState } from '@/features/resource-library/useResourceLibrary';
import type { FeedbackResourceCreationRequest, FeedbackResourceLinkTarget } from '@/features/feedback-authoring/resourceCreationContract';
import type { Player, ResourcePin, ResourceKind } from '@/lib/types';
import type { CoachResourceSearch, CoachReviewSearch, CoachShellSearch } from '@/router';

/** The page a feedback draft's composer was over when it added a Resource. */
type ReturnLocation={kind:'coach';search:CoachShellSearch}|{kind:'review';search:CoachReviewSearch}
  |{kind:'editor';location:ResourceEditorLocation};
type RecordingHint={match_id?:string;slot?:ResourceEditorSlot};
type Workspace={
  authoring:RefObject<FeedbackAuthoringHostHandle>;authoringActive:boolean;players:Player[];
  setOtherOverlay(active:boolean):void;
  openResource(kind:ResourceKind,request?:FeedbackResourceCreationRequest,hint?:RecordingHint):void;
  /** F2: back to where that feedback entry added a Resource; unknown after a reload. */
  back(feedbackEntryId:string):void;
  resourceLibrary: ResourceLibraryState; setResourceLibrary(state: ResourceLibraryState): void;
  openSavedResource(pin: ResourcePin, kind: ResourceKind): void;
  resourceAction(action: 'archive' | 'duplicate', pin: ResourcePin, kind: ResourceKind): void;
  resourceRecoveries: { id: string; label: string }[]; recoverResourceAction(entryId: string): void;
  resourceLinkRecoveries: { id: string; label: string }[]; recoverResourceLink(entryId: string): void;
  linkSavedResource(pin: ResourcePin, kind: ResourceKind): void;
};
type MetadataEntry = { id: string; owner: ResourceLibraryAction; pin?: ResourcePin; kind?: ResourceKind; action?: 'archive' | 'duplicate' };
type SavedLink = { id: string; context: DraftContext; boundary: ReturnType<typeof captureRecapBoundary>; pin: ResourcePin; kind: ResourceKind; targetId: string; busy: boolean; error: string | null; done: boolean };
type Choosing = { request: FeedbackResourceCreationRequest; settle(error?: unknown): void };
const EDITOR_PATH=/^\/coach\/resources\/([^/]+)$/;
const Context=createContext<(Omit<Workspace,'setOtherOverlay'> & {setOverlayOwner(owner:string,active:boolean):void})|null>(null);
export function useCanonicalCoachWorkspace():Workspace|null{
  const workspace=useContext(Context),owner=useId(),register=workspace?.setOverlayOwner;
  const setOtherOverlay=useCallback((active:boolean)=>register?.(owner,active),[register,owner]);
  useEffect(()=>()=>register?.(owner,false),[register,owner]);
  return useMemo(()=>workspace?{...workspace,setOtherOverlay}:null,[workspace,setOtherOverlay]);
}

/** DEV-only native integration. This parent outlives the /coach routes; route
 * disclosure never owns conversation writing or RHF forms. The Resource editor
 * (`/coach/resources/...`) owns its own sessions; this host only opens it. */
export function CanonicalCoachWorkspaceHost({children}:{children:ReactNode}) {
  const navigate=useNavigate(),router=useRouter(),location=useRouterState({select:state=>state.location});
  const context=useComposerDraftContext(),scope=useCanonicalScope('coach'),roster=useRoster();
  const recapScope=useRecapScope('coach'),matches=useMatches();
  const settings=useQuery({queryKey:publicAppSettingsKey(),queryFn:getAppSettings,staleTime:60_000});
  const tenantTeamName=useScopeStore(state=>state.active?.team.name);
  const authoring=useRef<FeedbackAuthoringHostHandle>(null);
  const [authoringActive,setAuthoringActive]=useState(false),[overlayOwners,setOverlayOwners]=useState<ReadonlySet<string>>(()=>new Set());
  const otherOverlay=overlayOwners.size>0;
  const setOverlayOwner=useCallback((owner:string,active:boolean)=>setOverlayOwners(previous=>{
    if(previous.has(owner)===active)return previous;
    const next=new Set(previous);if(active)next.add(owner);else next.delete(owner);return next;
  }),[]);
  const [recapOverlay,setRecapOverlay]=useState(false);
  const libraries = useRef(new Map<string, ResourceLibraryState>()), [, refreshLibrary] = useState(0);
  const libraryKey = JSON.stringify(context);
  const resourceLibrary = libraries.current.get(libraryKey) ?? initialResourceLibraryState();
  const setResourceLibrary = (state: ResourceLibraryState) => { libraries.current.set(libraryKey, state); refreshLibrary(value => value + 1); };
  const metadata = useRef(new Map<string, MetadataEntry>()), [activeMetadata, setActiveMetadata] = useState<string | null>(null);
  const [, refreshMetadata] = useState(0);
  const savedLinks = useRef(new Map<string, SavedLink>()), [activeLink, setActiveLink] = useState<string | null>(null);
  const [actionError, setActionError] = useState(false);
  const [choosing, setChoosing] = useState<Choosing | null>(null);
  // Feedback entry id → where its composer added a Resource (F2 return).
  const returns = useRef(new Map<string, ReturnLocation>());
  useEffect(() => {
    const changed = () => {
      // Observe the event itself, including A→B→A in one React batch. Keep
      // recovery records, but a retired record cannot own the foreground.
      savedLinks.current.forEach(link => link.boundary.current());
      metadata.current.forEach(entry => entry.owner.getState());
      refreshMetadata(value => value + 1);
    };
    const auth = useAuthStore.subscribe(changed), selection = useScopeStore.subscribe(changed), token = observeAuthTokenChanges(changed);
    return () => { auth(); selection(); token(); };
  }, []);
  const live=useRef(context);live.current=context;
  const players=roster.error||!scope?[]:(roster.data?.players??[]).filter(player=>player.team_id===scope.teamId
    && (player.season_id===null||player.season_id===scope.seasonId));
  const here=():ReturnLocation=>{
    if(location.pathname==='/coach')return {kind:'coach',search:{...location.search} as CoachShellSearch};
    const editor=EDITOR_PATH.exec(location.pathname),search=location.search as CoachResourceSearch;
    if(editor){
      const entry=search.entry??crypto.randomUUID();
      if(editor[1]!=='new')return {kind:'editor',location:{mode:'saved',resourceId:editor[1],entry,revision:search.revision}};
      if(search.kind)return {kind:'editor',location:{mode:'new',kind:search.kind,matchId:search.match_id,slot:search.slot,entry}};
      return {kind:'coach',search:{tab:'library',library_kind:'resources'}};
    }
    return {kind:'review',search:{...location.search} as CoachReviewSearch};
  };
  function back(feedbackEntryId:string){
    const to=returns.current.get(feedbackEntryId);
    if(!to)return;
    // Replace the finished editor, so Back from the draft's page does not reopen it.
    if(to.kind==='coach')void navigate({to:'/coach',search:to.search,replace:true});
    else if(to.kind==='review')void navigate({to:'/coach/review',search:to.search,replace:true});
    else void navigate(resourceEditorHref(to.location,true));
  }
  const mountEpoch=useRef(0);
  useEffect(()=>{const epoch=++mountEpoch.current;return()=>{
    // Strict Mode rehearses effects while keeping this owner. Retire on an
    // actual departure; each record independently fences auth/scope immediately.
    queueMicrotask(()=>{if(mountEpoch.current===epoch){metadata.current.forEach(entry=>entry.owner.dispose());savedLinks.current.forEach(link=>link.boundary.dispose());}});
  };},[]);
  /** A new Resource opens in its editor. The return target travels in the
   * entry's journal row, never in the URL (plan F2 step 2). */
  function createEntry(kind:ResourceKind,captured:DraftContext,target:ResourceEditorReturnTarget,hint?:RecordingHint){
    const entry=crypto.randomUUID();
    if(target.kind!=='standalone')initializeResourceEditorContinuation(captured,entry,target,kind);
    authoring.current?.suspend();
    void navigate(resourceEditorHref({mode:'new',kind,matchId:hint?.match_id,slot:hint?.slot,entry}));
  }
  function openResource(kind:ResourceKind,request?:FeedbackResourceCreationRequest,hint?:RecordingHint){
    const captured=live.current;
    if(!captured||!isCurrentDraftContext(captured)||request?.signal.aborted)return;
    if(request&&(request.scope.actorId!==captured.actorId||request.scope.clubId!==captured.clubId
      ||request.scope.teamId!==captured.teamId||request.scope.seasonId!==captured.seasonId))return;
    if(request)returns.current.set(request.entryId,here());
    const target=request?{kind:'feedback' as const,entry_id:request.entryId,creation_attempt_id:request.creationAttemptId}:{kind:'standalone' as const};
    createEntry(kind,captured,target,hint);
  }
  /** The composer's Create new Resource: a board opens directly; a moment or
   * clip picks its match and recording first (plan F2 step 1). */
  function createResource(request:FeedbackResourceCreationRequest):void|Promise<void>{
    if(request.kind==='tactical_board'){openResource('tactical_board',request);return;}
    return new Promise<void>((resolve,reject)=>setChoosing({request,settle:error=>error===undefined?resolve():reject(error)}));
  }
  useEffect(()=>{
    if(!choosing)return;
    const cancel=()=>{setChoosing(null);choosing.settle();};
    if(choosing.request.signal.aborted){cancel();return;}
    choosing.request.signal.addEventListener('abort',cancel,{once:true});
    return()=>choosing.request.signal.removeEventListener('abort',cancel);
  },[choosing]);
  /** A composer pin opens its exact version in a new tab, so the draft stays
   * open here; an older version opens read-only there (plan F4). */
  function openPinned(pin:ResourcePin){
    const href=router.buildLocation(resourceEditorHref({mode:'saved',resourceId:pin.resource_id,revision:pin.revision_id,entry:crypto.randomUUID()})).href;
    window.open(href,'_blank','noopener');
  }
  /** A saved Resource opens at its current version (plan F4). */
  function ensureBoard(pin:ResourcePin){
    const captured=live.current;if(!captured||!isCurrentDraftContext(captured))return;
    void navigate(resourceEditorHref({mode:'saved',resourceId:pin.resource_id,entry:crypto.randomUUID()}));
  }
  function resourceAction(action: 'archive' | 'duplicate', pin: ResourcePin, kind: ResourceKind) {
    const captured = live.current; if (!captured || !isCurrentDraftContext(captured)) return;
    const pending = [...metadata.current.values()].find(entry => entry.pin?.resource_id === pin.resource_id
      && entry.owner.getState().current && (entry.owner.getState().busy || entry.owner.getState().command));
    if (pending) { setActiveMetadata(pending.id); return; }
    const id = crypto.randomUUID(); let owner: ResourceLibraryAction;
    try { owner = createResourceLibraryAction(captured, id); } catch { setActionError(true); return; }
    metadata.current.set(id, { id, owner, pin: { resource_id: pin.resource_id, revision_id: pin.revision_id }, kind, action });
    owner.subscribe(() => refreshMetadata(value => value + 1)); setActiveMetadata(id);
    void owner.start(action, pin, kind).catch(() => {});
  }
  function recoverResourceAction(id: string) {
    const captured = live.current; if (!captured || !isCurrentDraftContext(captured)) return;
    const existing = metadata.current.get(id);
    if (!existing?.owner.getState().current) {
      existing?.owner.dispose();
      try { const owner = createResourceLibraryAction(captured, id), row = loadResourceEditorContinuation(captured, id);
        const command = row?.pending ?? row?.accepted?.command;
        metadata.current.set(id, { id, owner, pin: row?.origin ?? undefined, kind: row ? resourceEditorContinuationKind(row) : undefined,
          action: command?.action === 'archive' || command?.action === 'duplicate' ? command.action : undefined }); owner.subscribe(() => refreshMetadata(value => value + 1)); }
      catch { return; }
    }
    setActiveMetadata(id);
  }
  const resourceRecoveries: Workspace['resourceRecoveries'] = [];
  if (context) {
    try { for (const row of listResourceEditorContinuations(context)) {
      const command = row.pending ?? row.accepted?.command;
      // An editor's settled archive or duplicate needs no recovery here; list
      // only unresolved ones and the Library's own actions of this tab.
      if (row.return_target.kind === 'standalone' && (command?.action === 'archive' || command?.action === 'duplicate')
        && (row.pending || metadata.current.has(row.entry_id)))
        resourceRecoveries.push({ id: row.entry_id, label: `${command.action === 'archive' ? 'Archive' : 'Duplicate'} Resource · ${row.pending ? 'Check original result' : 'Saved result'}` });
    } } catch { /* A blocked journal cannot be guessed or automatically dispatched. */ }
  }
  function linkSavedResource(pin: ResourcePin, kind: ResourceKind) {
    const captured = live.current, host = authoring.current; if (!captured || !isCurrentDraftContext(captured) || !host) return;
    const kept = [...savedLinks.current.values()].find(link => !link.done && link.boundary.current() && link.kind === kind
      && link.pin.resource_id === pin.resource_id && link.pin.revision_id === pin.revision_id);
    if (kept) { setActiveLink(kept.id); return; }
    host.chooseResourceDestination((targetId, activate) => {
      const job: SavedLink = { id: crypto.randomUUID(), context: captured, boundary: captureRecapBoundary({ ...captured, view: 'coach' }), pin: { resource_id: pin.resource_id, revision_id: pin.revision_id }, kind, targetId, busy: true, error: null, done: false };
      savedLinks.current.set(job.id, job);
      activate(target => { void admitSaved(job, target); }, () => {
        job.busy = false; job.error = 'Your Resource is saved. The selected Feedback entry could not be opened.';
        if (job.boundary.current()) { authoring.current?.suspend(); setActiveLink(job.id); }
      });
    }, () => {});
  }
  async function admitSaved(job: SavedLink, target: FeedbackResourceLinkTarget) {
    if (!job.boundary.current()) { job.busy = false; return; }
    if (target.entryId !== job.targetId || target.signal.aborted) {
      job.busy = false; job.error = 'The original Feedback entry is unavailable. Your saved Resource and writing are kept.';
      authoring.current?.suspend(); setActiveLink(job.id); return;
    }
    job.busy = true; job.error = null; refreshMetadata(value => value + 1);
    try {
      const outcome = await target.admitPin({ kind: job.kind, pin: job.pin });
      if (!job.boundary.current()) return;
      if (outcome.status === 'retained') {
        job.error = outcome.reason === 'replacement-required' ? 'A different version is selected. Use its explicit Update version action; this link was not replaced.'
          : outcome.reason === 'resource-limit' ? 'This Feedback already has ten Resources. Its selections are kept.' : 'Resource saved; link not completed. Your original Feedback writing is kept.';
        if (authoring.current?.isEntryActive(job.targetId)) { authoring.current.suspend(); setActiveLink(job.id); }
      } else { job.done = true; setActiveLink(value => value === job.id ? null : value); }
    } catch {
      job.error = 'Resource saved; link not completed. Your original Feedback writing is kept.';
      if (job.boundary.current() && authoring.current?.isEntryActive(job.targetId)) { authoring.current.suspend(); setActiveLink(job.id); }
    }
    finally { job.busy = false; refreshMetadata(value => value + 1); }
  }
  function retrySaved(job: SavedLink) {
    if (job.busy || !job.boundary.current()) return;
    setActiveLink(null); job.busy = true;
    const cancelled = () => {
      job.busy = false; job.error = 'The original Feedback entry is unavailable. Your saved Resource and writing are kept.';
      if (job.boundary.current()) { authoring.current?.suspend(); setActiveLink(job.id); refreshMetadata(value => value + 1); }
    };
    if (!authoring.current?.activateEntry(job.targetId, target => { void admitSaved(job, target); }, cancelled)) cancelled();
  }
  const heldMetadata = activeMetadata ? metadata.current.get(activeMetadata) : null;
  const heldLink = activeLink ? savedLinks.current.get(activeLink) : null;
  const metadataEntry = heldMetadata?.owner.getState().current ? heldMetadata : null;
  const savedLink = heldLink?.boundary.current() ? heldLink : null;
  useEffect(() => {
    if (activeMetadata && !metadataEntry) setActiveMetadata(null);
    if (activeLink && !savedLink) setActiveLink(null);
  }, [activeMetadata, metadataEntry, activeLink, savedLink]);
  const coachPage=location.pathname==='/coach';
  const target=coachPage?feedbackNavigationTarget(location.search):null;
  return <Context.Provider value={{authoring,authoringActive,players,setOverlayOwner,openResource,back,
    resourceLibrary, setResourceLibrary, openSavedResource: pin => ensureBoard(pin),
    resourceAction, resourceRecoveries, recoverResourceAction, linkSavedResource,
    resourceLinkRecoveries: [...savedLinks.current.values()].filter(link => !link.done && link.boundary.current()).map(link => ({ id: link.id, label: link.busy ? 'Original link is still pending' : 'Original Feedback writing is kept' })),
    recoverResourceLink: id => { const link = savedLinks.current.get(id); if (link?.boundary.current()) setActiveLink(id); } }}>
    <CanonicalRecapHost scope={recapScope} matches={matches.data?.filter(match=>match.season_id===recapScope?.seasonId)}
      clubLogoUrl={getUploadedClubLogoUrl(settings.data)} tenantTeamName={tenantTeamName}
      active={coachPage&&!authoringActive&&!otherOverlay&&!actionError&&!metadataEntry&&!savedLink}
      requestedRecapId={coachPage?(location.search as CoachShellSearch).recap_id:undefined}
      onCloseDestination={()=>void navigate({to:'/coach',search:{...(location.search as CoachShellSearch),recap_id:undefined}})}
      onOverlayChange={setRecapOverlay}>{children}</CanonicalRecapHost>
    <FeedbackConversationHost view="coach" target={target} players={players}
      invalid={coachPage&&!target&&!!(location.search.feedback_id||location.search.feedback_player_id)}
      suspended={!coachPage||authoringActive||otherOverlay||recapOverlay||actionError||!!metadataEntry||!!savedLink} onClose={()=>void navigate({to:'/coach',search:{...(location.search as CoachShellSearch),feedback_id:undefined,feedback_player_id:undefined}})}
      onEdit={entry=>authoring.current?.edit(entry)}/>
    <FeedbackAuthoringHost ref={authoring} players={players.filter(player=>player.active).map(player=>({id:player.id,label:player.display_name}))}
      onActiveChange={setAuthoringActive} onCreateResource={createResource} onOpenResource={openPinned}/>
    {choosing && <ResourceKindChooser kind={choosing.request.kind} note="The Resource is pinned to your feedback draft when you save it."
      onClose={() => { setChoosing(null); choosing.settle(); }}
      onOpen={(kind, matchId, slot) => {
        setChoosing(null);
        try { openResource(kind, choosing.request, { match_id:matchId, slot }); choosing.settle(); } catch (error) { choosing.settle(error); }
      }}/>}
    {metadataEntry && <ResourceActionDialog entry={metadataEntry} onClose={() => setActiveMetadata(null)}
      onEdit={pin => { setActiveMetadata(null); ensureBoard(pin); }}
      onLink={(pin, kind) => { setActiveMetadata(null); linkSavedResource(pin, kind); }} />}
    {actionError && <CoachModal title="Resource action unavailable" onClose={() => setActionError(false)}><p role="alert">The recovery record could not be prepared. No Resource command was sent. Close and try the action again.</p></CoachModal>}
    {savedLink && savedLink.boundary.current() && <CoachModal title="Resource saved" onClose={() => setActiveLink(null)}>
      <p role="alert">{savedLink.error}</p><Button variant="outline" disabled={savedLink.busy} onClick={() => retrySaved(savedLink)}>Retry original link</Button>
      <p className="mt-3 text-xs text-ink-muted">Only linking is retried. The Resource will not be saved again.</p>
    </CoachModal>}
  </Context.Provider>;
}

function ResourceActionDialog({ entry, onClose, onEdit, onLink }: { entry: MetadataEntry; onClose(): void;
  onEdit(pin: ResourcePin, kind: ResourceKind): void; onLink(pin: ResourcePin, kind: ResourceKind): void }) {
  useSyncExternalStore(entry.owner.subscribe, entry.owner.revision, entry.owner.revision);
  const state = entry.owner.getState();
  if (!state.current) return null;
  const retry = () => { void (state.command ? entry.owner.retry() : entry.pin && entry.action && entry.kind
    ? entry.owner.start(entry.action, entry.pin, entry.kind) : Promise.reject(Error('Original action unavailable'))).catch(() => {}); };
  return <CoachModal title={state.action === 'archive' ? 'Archive Resource' : 'Duplicate Resource'} onClose={onClose}>
    {state.busy ? <p role="status">Checking the Resource result…</p> : state.receipt ? <>
      <p role="status">{state.action === 'archive' ? 'Resource archived. Existing Feedback keeps its selected version.' : 'Resource duplicated and saved independently.'}</p>
      {state.action === 'duplicate' && state.kind && <div className="mt-3 flex gap-2"><Button onClick={() => onEdit(state.receipt!, state.kind!)}>Open editor</Button><Button variant="outline" onClick={() => onLink(state.receipt!, state.kind!)}>Link to Feedback</Button></div>}
      <Button variant="ghost" onClick={() => { entry.owner.dismiss(); onClose(); }}>Done</Button>
    </> : <>
      <p role="alert">{state.command?.status === 'accepted' ? 'The server accepted this operation. Its local recovery record still needs to be confirmed.' : state.error ?? 'An earlier Resource operation needs its original result checked.'}</p>
      <Button variant="outline" onClick={retry}>{state.command ? 'Check original operation' : 'Retry action'}</Button>
      {state.command?.status === 'rejected' && <Button variant="ghost" onClick={() => { entry.owner.releaseRejected(); onClose(); }}>Keep Resource and close</Button>}
    </>}
  </CoachModal>;
}
